<template>
    <div id="printer-settings">
        <section v-if="hasAccess">
            <div class="label main">
                <h6>{{$t('printers.modal.printerSettings.announcement')}}</h6>
                <input v-model="announcement" type="text" class="inp"
                       :placeholder="$t('printers.modal.printerSettings.announcementPlaceholder')"/>
            </div>
        </section>
        <section v-if="hasAccess">
            <h6>
                <b-form-checkbox v-model="enableFilamentTracking" switch
                                 :class="{ 'text-muted' : !enableFilamentTracking }">{{$t('printers.modal.printerSettings.filamentTracking')}}
                </b-form-checkbox>
            </h6>
            <div v-if="enableFilamentTracking && filaments">
                <div class="label modal--printer-settings__filament-item" v-for="n in extruderCount" :key="n">
                    <color-picker filament :value="filaments[n-1].c"
                                  @input="handleUpdateColor($event, n-1)"></color-picker>
                    <b-select class="inp inp--small inp--fill ml-3" v-model="filaments[n-1].m">
                        <option v-for="material in materialsSortedList" :key="material.key"
                                :value="material.key">{{material.name}}
                        </option>
                    </b-select>
                    <label class="ml-3">
                        <span>{{$t('printers.modal.printerSettings.filament.left')}}</span>
                        <input type="text" class="inp inp--small inp--fill"
                               v-model="filaments[n-1].w"/>
                        <span>{{$t('measure.shortcut.gram')}}</span>
                    </label>
                    <label class="ml-3" v-if="showNozzles">
                        <span>{{$t('printers.modal.printerSettings.filament.nozzle')}}</span>
                        <input type="text" class="inp inp--small"
                               v-model="nozzles[n-1]"/>
                        <span>{{$t('measure.shortcut.millimeter')}}</span>
                    </label>
                </div>
            </div>
        </section>
        <section>
            <h6>{{$t('printers.modal.printerSettings.displayTitle')}}</h6>
            <div class="label main">
                <input type="text" class="inp" v-model="printerName"/>
                <b-select v-if="hasAccess" class="sel inp" v-model="printerType">
                    <option v-for="(type, index) in printerTypes" :key="index" :value="type.alias">{{type.name}}
                </option>
            </b-select>
            </div>
        </section>
        <section v-if="hasAccess">
            <label>
                <b-checkbox switch v-model="active" :class="{ 'text-muted' : !active }">{{$t('printers.modal.printerSettings.active')}}</b-checkbox>
            </label> <label>
            <b-form-checkbox v-model="enableQueue" switch :class="{ 'text-muted' : !enableQueue }">{{$t('printers.modal.printerSettings.enableQueue')}}
            </b-form-checkbox>
        </label> <label>
            <b-checkbox switch v-model="enableClearBedNotification" :class="{ 'text-muted' : !enableClearBedNotification }">
              {{$t('printers.modal.printerSettings.enableClearBed')}}
            </b-checkbox>
        </label> <label>
            <b-checkbox switch v-model="enableStartNextJob" :class="{ 'text-muted' : !enableStartNextJob }">
              {{$t('printers.modal.printerSettings.startNextJobButton')}}
            </b-checkbox>
        </label>
        </section>
        <section class="cam" v-if="hasAccess">
            <div class="label">
                <h6>
                    <b-form-checkbox v-model="enableCamera" @change="handleCameraSwitch" switch
                                     :class="{ 'text-muted' : !enableCamera }">
                        {{$t('printers.modal.printerSettings.camera')}}
                    </b-form-checkbox>
                </h6>
                <!--<b-form-radio v-model="cameraType" size="sm" v-if="enableCamera" @change="handleChangeCameraType"-->
                              <!--value="Dual camera">{{$t('printers.modal.printerSettings.dualCamera')}}-->
                <!--</b-form-radio>-->
                <!--<b-form-radio v-model="cameraType" size="sm" v-if="enableCamera" @change="handleChangeCameraType"-->
                              <!--value="Pi camera">{{$t('printers.modal.printerSettings.piCamera')}}-->
                <!--</b-form-radio>-->
                <!--<b-form-radio v-model="cameraType" size="sm" v-if="enableCamera" @change="handleChangeCameraType"-->
                              <!--value="Multi camera">{{$t('printers.modal.printerSettings.multiCamera')}}-->
                <!--</b-form-radio>-->
            </div>
            <div class="cam__preview" v-if="enableCamera">
                <printer-camera-picker ref="cameraPicker" :enabled="enableCamera" :printer-id="printer.printer_id"
                                       :webcam-id="printer.webcam_id"></printer-camera-picker>

                <b-button variant="-ternary btn--outline" size="-small" @click="handleResetCamera">
                    {{$t('printers.modal.printerSettings.resetModule')}}
                </b-button>
            </div>
        </section>
        <section v-if="hasAccess">
            <div class="label main">
                <h6>{{$t('printers.modal.printerSettings.delete')}}</h6>
                <input type="text" class="inp inp--small" :state="emailToDeleteValid" v-model="emailToDelete"
                       @change="handleChangeEmailToDelete" :placeholder="$t('printers.modal.printerSettings.yourEmail')"/>
                <b-button variant="-ternary btn--outline" size="-small" v-if="isOffline(printer.state)"
                          @click="handleOpenDeletePrinter">{{$t('printers.modal.printerSettings.removePrinter')}}
                </b-button>
                <span v-else v-b-tooltip.hover :title="$t('printers.modal.printerSettings.removeOnlinePrinterTooltip')">
                    <b-button variant="-ternary btn--outline btn--disabled" size="-small" disabled>
                        {{$t('printers.modal.printerSettings.removePrinter')}}
                    </b-button>
                </span>
            </div>
        </section>
        <section v-if="owner">
            <h6>{{$t('printers.modal.printerSettings.owner')}} <span class="text--500" v-b-tooltip :title="$t('printers.modal.printerSettings.changeAccountNote')"><i
                    class="icon icon--crown">
            <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" data-prefix="fas"
                 data-icon="crown" class="svg-inline--fa fa-crown fa-w-20" role="img" viewBox="0 0 640 512">
              <path d="M528 448H112c-8.8 0-16 7.2-16 16v32c0 8.8 7.2 16 16 16h416c8.8 0 16-7.2 16-16v-32c0-8.8-7.2-16-16-16zm64-320c-26.5 0-48 21.5-48 48 0 7.1 1.6 13.7 4.4 19.8L476 239.2c-15.4 9.2-35.3 4-44.2-11.6L350.3 85C361 76.2 368 63 368 48c0-26.5-21.5-48-48-48s-48 21.5-48 48c0 15 7 28.2 17.7 37l-81.5 142.6c-8.9 15.6-28.9 20.8-44.2 11.6l-72.3-43.4c2.7-6 4.4-12.7 4.4-19.8 0-26.5-21.5-48-48-48S0 149.5 0 176s21.5 48 48 48c2.6 0 5.2-.4 7.7-.8L128 416h384l72.3-192.8c2.5.4 5.1.8 7.7.8 26.5 0 48-21.5 48-48s-21.5-48-48-48z"/>
            </svg>
          </i> {{owner.email}}
          </span></h6>
        </section>

        <b-modal v-model="openDeletePrinter" size="lg">
            <delete-printer-confirmation ref="deletePrinterComponent" :printer="printer"></delete-printer-confirmation>
            <template v-slot:modal-footer="{ cancel, ok }">
                <div class="modal__footer--left">
                    <b-button variant="-ternary btn--outline" @click="cancel()">
                        {{$t('buttons.cancel')}}
                    </b-button>
                </div>
                <div class="modal__footer--right">
                    <b-button variant="-primary" @click="handleDeletePrinter(ok)">
                        {{$t('printers.modal.confirmDeletePrinter.delete')}}
                    </b-button>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
    import Vue from 'vue';
    import {mapActions, mapGetters} from 'vuex';

    import PrinterMixin from "@/mixins/printer";
    import UserMixin from "@/mixins/user";
    import MaterialsMixin from "@/mixins/materials";

    import PrinterCameraPicker from "../camera/PrinterCameraPicker";
    import ColorPicker from "../../ColorPicker";
    import DeletePrinterConfirmation from "./DeletePrinterConfirmation";
    //import OpenChatButton from "../../chat/OpenChatButton";

    export default {
        name: 'printer-settings',
        props: ['printer'],
        mixins: [PrinterMixin, UserMixin, MaterialsMixin],
        components: {
            //OpenChatButton,
            DeletePrinterConfirmation,
            ColorPicker,
            PrinterCameraPicker
        },
        data() {
            return {
                printerName: '',
                printerTypes: [],
                printerType: '',
                active: true,
                enableClearBedNotification: false,
                enableStartNextJob: false,
                enableMonitoring: false,
                enableFilamentTracking: false,
                filaments: null,
                nozzles: [],
                announcement: '',
                settings: {
                    filament: false
                },
                owner: null,
                enableQueue: true,
                enableCamera: false,
                cameraType: null,
                webcamId: 0,

                emailToDelete: '',
                emailToDeleteValid: null,
                openDeletePrinter: false,

                //TODO: get materials list from slicer3 materialManager
                materaialsList: {
                    ABS: "ABS",
                    Breakaway: "Breakaway",
                    CFF_CPE: "CFF CPE",
                    CFF_PA: "CFF PA",
                    CPE: "CPE",
                    'CPE+': "CPE+",
                    GFF_CPE: "GFF CPE",
                    GFF_PA: "GFF PA",
                    HIPS: "HIPS",
                    Nylon: "Nylon",
                    PC: "PC",
                    PETG: "PETG",
                    PLA: "PLA",
                    PP: "PP",
                    PVA: "PVA",
                    TPU_95A: "TPU 95A",
                    Tough_PLA: "Tough PLA",
                }
            }
        },
        created() {
            this.getAvailablePrinterTypes(this.printer.printer_id).then(({data}) => {
                if (data.result) {
                    this.printerTypes = data.message;
                }
            });
            this.getPrinterOwner(this.printer.printer_id).then(({data}) => {
                if (data.result) {
                    this.owner = data.message;
                }
            });
            if (this.printer.share_printer_name) {
                this.printerName = this.printer.share_printer_name;
            } else {
                this.printerName = this.printer.real_printer_name;
            }
            this.printerType = this.printer.printer_type;
            this.active = this.printer.active === 't';
            this.enableClearBedNotification = this.printer.clear_bed_notification === 't';
            this.enableStartNextJob = this.printer.next_job_button === 't';
            this.enableMonitoring = this.printer.flags & this.getPrintSettings.flags.enable_monitoring;
            this.enableFilamentTracking = this.printer.filament_tracking === 't';
            if (this.printer.filaments) {
                this.filaments = this.printer.filaments;
            } else {
                this.filaments = [];
                for (let ex = 0; ex < this.extruderCount; ex++) {
                    this.filaments[ex] = {c: '', m: '', w: 0};
                }
            }
            this.nozzles = this.printer.nozzles ? JSON.parse(this.printer.nozzles) : [];
            for (let ex = 0; ex < this.extruderCount; ex++) {
                if (this.nozzles[ex] === undefined) {
                    this.nozzles[ex] = '';
                }
            }
            this.announcement = this.printer.notes;
            this.enableQueue = !(this.printer.flags & this.getPrintSettings.flags.disable_queue);
            this.enableCamera = this.printer.camera_type !== false && this.printer.camera_type !== null && this.printer.camera_type !== 'Disable camera';
            this.cameraType = this.printer.camera_type;
            this.webcamId = this.printer.webcam_id;
            
            this.materaialsList = {};
            for (let key in window.materials_data) {
                this.materaialsList[key] = window.materials_data[key].metadata.name.material;
            }
        },
        computed: {
            ...mapGetters(['isDualExtruderPrinter', 'isKodak', 'getPrintSettings', 'userData']),

            extruderCount() {
                return this.isDualExtruderPrinter(this.printer.printer_type_id) ? 2 : 1;
            },

            showNozzles() {
                return !this.isKodak(this.printer.printer_type_id);
            },

            hasAccess() {
                return this.canAccessAsOwner(this.printer.printer_ownership_id);
            }
        },
        methods: {
            ...mapActions(['getAvailablePrinterTypes', 'getPrinterOwner', 'updatePrinterSettings', 'resetCamera', 'updateCameraType']),

            handleCameraSwitch(value) {
                this.cameraType = value ? 'Dual camera' : 'Disable camera';
                this.handleChangeCameraType(this.cameraType);
            },

            handleResetCamera() {
                this.resetCamera(this.printer.printer_id);
            },

            handleUpdatePrinterSettings() {
                let data = {};
                if (!this.hasAccess) {
                    data = {name: this.printerName};
                } else {
                    data = {
                        id: this.printer.printer_id,
                        name: this.printerName,
                        type: this.printerType,
                        camera_type: this.enableCamera ? this.cameraType : 'Disable camera',
                        webcam_id: this.enableCamera ? this.$refs['cameraPicker'].getSelectedCamera() : null,
                        filament_tracking: this.enableFilamentTracking,
                        clear_bed_notification: this.enableClearBedNotification,
                        next_job_button: this.enableStartNextJob,
                        disable_queue: !this.enableQueue,
                        enable_monitoring: this.enableMonitoring
                    };
                    const currentActiveFlag = this.active ? 't' : 'f';
                    if (currentActiveFlag !== this.printer.active) {
                        data.active = this.active;
                    }
                    for (let i in this.filaments) {
                        data['filaments[' + i + '][c]'] = this.filaments[i].c;
                        data['filaments[' + i + '][m]'] = this.filaments[i].m;
                        data['filaments[' + i + '][w]'] = this.filaments[i].w;
                    }
                    for (let i in this.nozzles) {
                        data['nozzles[' + i + ']'] = this.nozzles[i];
                    }
                    if (this.printer.notes !== this.announcement) {
                        data['notes'] = this.announcement;
                    }
                }
                this.updatePrinterSettings(data);
            },

            handleChangeCameraType(value) {
                this.updateCameraType({
                    printerId: this.printer.printer_id,
                    cameraType: value
                });
            },

            handleUpdateColor(color, ex) {
                Vue.set(this.filaments, ex, Object.assign({}, this.filaments[ex], {c: color}));
            },

            handleChangeEmailToDelete() {
                this.emailToDeleteValid = null;
            },

            handleOpenDeletePrinter() {
                if (this.emailToDelete.trim().toLowerCase() !== this.userData.email.trim().toLowerCase()) {
                    this.emailToDeleteValid = false;
                } else {
                    this.openDeletePrinter = true;
                }
            },

            handleDeletePrinter(ok) {
                this.$refs.deletePrinterComponent.handleDeletePrinter();
                ok();
                this.$emit('close');
            }
        }
    }
</script>
<style lang="scss">
    .modal--printer-settings {
        .modal-dialog {
            max-width: 600px;
        }

        section {
            margin: 12px 0 24px;
            .btn {
                margin-left: 12px;
            }
        }
        h6 {
            margin: 7px 12px 8px 0;
        }
        .label {
            height: 42px;
            .custom-radio {
                margin-right: 24px;
            }
            &.main {
                margin-bottom: 12px;
                .inp {
                    width: 60%;
                }
                .sel {
                    margin-left: 12px;
                    width: 40%;
                }
            }
        }
        .cam {
            .label {
                h6 {
                    margin-right: 32px;
                }
            }
            p {
                margin: 4px 120px 12px 0;
            }

            &__preview {
                display: flex;
                align-items: flex-end;
            }
        }

        &__filament-item {
            justify-content: space-between;
            height: 42px;
            margin-bottom: .5rem;
            label {
                margin: 0;
                width: unset;
                span {
                    margin-right: 5px !important;
                }
                .inp {
                    max-width: 38px;
                    &--fill {
                        max-width: 100px;
                    }
                }
            }
            .sel {
                width: 190px;
            }
        }
    }
</style>
